import { supabase } from "@/integrations/supabase/client";
import { Profile } from "./types";

export async function fetchUserProfile(userId: string): Promise<Profile | null> {
  try {
    const { data, error } = await supabase.from("profiles").select("*").eq("id", userId).single();

    if (error) {
      console.error("Error fetching profile:", error);
      return null;
    }

    if (!data) {
      return null;
    }

    return {
      ...data,
      // Keep display_name from database if it exists, otherwise compute from first_name and last_name
      display_name:
        data.display_name ||
        (data.first_name && data.last_name
          ? `${data.first_name} ${data.last_name}`
          : data.first_name || data.last_name || null),
      username: data.first_name && data.last_name ? `${data.first_name}-${data.last_name}` : null,
    };
  } catch (error) {
    console.error("Error fetching profile:", error);
    return null;
  }
}

export async function updateUserProfile(userId: string, updates: Partial<Profile>) {
  const { error } = await supabase.from("profiles").update(updates).eq("id", userId);

  if (error) {
    throw error;
  }
}

// Point transaction functions that use database functions for proper tracking
export async function earnPoints(
  userId: string,
  amount: number,
  description?: string,
  metadata?: Record<string, unknown>
) {
  const { error } = await supabase.rpc("earn_points", {
    p_user_id: userId,
    p_amount: amount,
    p_description: description || null,
    p_metadata: metadata || null,
  });

  if (error) {
    throw error;
  }
}

export async function spendPoints(
  userId: string,
  amount: number,
  description?: string,
  metadata?: Record<string, unknown>
) {
  const { error } = await supabase.rpc("spend_points", {
    p_user_id: userId,
    p_amount: amount,
    p_description: description || null,
    p_metadata: metadata || null,
  });

  if (error) {
    throw error;
  }
}

// Self-service role switch. The DB rejects direct profiles.role writes from the
// authenticated role (privilege-escalation guard); switch_role validates the
// target role against the caller's allowed roles and applies it for auth.uid().
export async function updateUserRole(role: "student" | "teacher" | "admin") {
  const { error } = await (supabase.rpc as CallableFunction)("switch_role", {
    p_role: role,
  });

  if (error) {
    throw error;
  }
}
